import React, { useState, useEffect } from 'react';
import { 
  LayoutDashboard, LogOut, Search, User, MapPin, DollarSign, Clock, 
  ChevronRight, ArrowLeft, Edit, Send, CheckCircle, Info, X, FileText, Download, Users, Database, ToggleLeft, ToggleRight, Bell, File, Menu
} from 'lucide-react';
import axios from 'axios';
import './VendorStyles.css';

// --- IMPORTS ---
import VendorProfileForm from './VendorProfileForm';
import CandidateSubmissionForm from './CandidateSubmissionForm'; // Standard Form
import BeelineSubmissionForm from './BeelineSubmissionForm';     // NEW: Beeline Wizard
import VendorSubmissionsDatabase from './VendorSubmissionsDatabase';
import PostedJobs from '../Adminfiles/PostedJobs';

export default function VendorDashboard({ onLogout }) {
  // Navigation States
  const [activeTab, setActiveTab] = useState('browse-jobs'); 
  const [jobFilter, setJobFilter] = useState('open'); 
  const [viewState, setViewState] = useState('LIST'); 
  const [sidebarOpen, setSidebarOpen] = useState(false); 
  
  // --- TOGGLE STATE: Standard vs Beeline ---
  const [showBeeline, setShowBeeline] = useState(false);

  const [selectedJob, setSelectedJob] = useState(null);
  const [showModal, setShowModal] = useState(false); 
  
  const [profileData, setProfileData] = useState(null);
  const [mockJobs, setMockJobs] = useState([]);
  const [loadingProfile, setLoadingProfile] = useState(true);

  // --- State for Viewing Submissions ---
  const [submittedCandidates, setSubmittedCandidates] = useState([]);
  const [loadingCandidates, setLoadingCandidates] = useState(false);

  // --- URL HELPER FOR EXPIRED TOKENS ---
  const getFreshFileUrl = (url) => {
    if (!url) return '#';
    return `${process.env.REACT_APP_API_BASE_URL}/api/candidates/view-file?url=${encodeURIComponent(url)}`;
  };

  // --- 1. DEPENDENCY EFFECT: Fetch Jobs when Profile ID is ready ---
  useEffect(() => {
    if (profileData && profileData._id) {
        fetchJobs(profileData._id);
    }
  }, [profileData]);

  // --- 2. INITIAL LOAD ---
  useEffect(() => {
    fetchProfileData();
  }, []);

  // --- 3. ROBUST PROFILE FETCH ---
  const fetchProfileData = async () => {
    try {
        const storedData = localStorage.getItem('vendorProfileData');
        
        if (storedData) {
            const parsed = JSON.parse(storedData);
            setProfileData(parsed); 
            setLoadingProfile(false); 

            // Background Refresh
            try {
                const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/vendors/${parsed._id}`);
                setProfileData(response.data);
                localStorage.setItem('vendorProfileData', JSON.stringify(response.data));
            } catch (apiError) {
                console.warn("Background profile refresh failed", apiError);
            }
        } else {
            setLoadingProfile(false);
        }
    } catch (error) {
        console.error("Error fetching vendor profile:", error);
        setLoadingProfile(false);
    }
  };
  
  // --- 4. FETCH JOBS ---
  const fetchJobs = async (partnerId) => {
    try {
      if (!partnerId) return;
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/jobs`, {
          params: { partnerId: partnerId } 
      }); 
      setMockJobs(response.data);
    } catch (error) {
      console.error("Error fetching jobs for vendor:", error);
    }
  };

  const isJobOpen = (job) => {
    if (!job.applyByDate) return true;
    const deadline = new Date(job.applyByDate);
    if (job.applyByTime) {
      const [h, m] = job.applyByTime.split(':');
      deadline.setHours(Number(h || 23), Number(m || 59), 59, 999);
    } else {
      deadline.setHours(23, 59, 59, 999);
    }
    return new Date() <= deadline;
  };

  // --- FILTER LOGIC (Separates Standard / Beeline) ---
  const filteredJobs = mockJobs.filter(job => {
    // 1. Filter by Open/Closed status
    const isOpen = isJobOpen(job);
    const statusMatch = jobFilter === 'open' ? isOpen : !isOpen;

    // 2. Filter by Toggle
    const typeMatch = showBeeline ? (job.isBeeline === true) : (!job.isBeeline);

    return statusMatch && typeMatch;
  });

  // --- NOTIFICATION COUNT ---
  const beelineNotificationCount = mockJobs.filter(job => job.isBeeline === true && isJobOpen(job)).length;

  const formatDate = (dateString) => {
    if (!dateString) return '--';
    if (dateString.includes('T')) return dateString.split('T')[0];
    return dateString;
  };

  // --- ACTIONS ---

  const handleJobClick = (job) => {
    setSelectedJob(job);
    setShowModal(true); 
  };

  const handleProfileUpdate = async (formData) => {
    try {
        if (!profileData || !profileData._id) return;

        const res = await axios.put(
            `${process.env.REACT_APP_API_BASE_URL}/api/vendors/${profileData._id}`, 
            formData, 
            { headers: { 'Content-Type': 'multipart/form-data' } }
        );

        setProfileData(res.data);
        localStorage.setItem('vendorProfileData', JSON.stringify(res.data));
        
        alert("Profile Updated Successfully!");
        setViewState('LIST');
        setActiveTab('profile');
    } catch (err) {
        console.error("Update Error:", err);
        alert("Failed to update profile. Check console.");
    }
  };

  const handleBack = () => {
    // Handle back from any sub-view
    if (viewState === 'SUBMIT_CANDIDATE' || viewState === 'SUBMIT_BEELINE' || viewState === 'VIEW_SUBMISSIONS') {
        setViewState('LIST'); 
        setSelectedJob(null);
    } else if (viewState === 'EDIT_PROFILE') {
        setViewState('LIST');
    } else {
        setViewState('LIST');
        setSelectedJob(null);
    }
  };

  const handleStartSubmission = () => {
    setShowModal(false); 
    
    // --- SMART ROUTING: CHECK IF BEELINE JOB ---
    if (selectedJob?.isBeeline) {
        setViewState('SUBMIT_BEELINE'); // Go to 3-Step Wizard
    } else {
        setViewState('SUBMIT_CANDIDATE'); // Go to Standard Form
    }
  };

  // --- View Submissions Handler ---
  const handleViewSubmissions = async () => {
    if (!selectedJob || !profileData) return;
    
    setLoadingCandidates(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/vendors/${profileData._id}/jobs/${selectedJob._id}/candidates`
      );
      setSubmittedCandidates(response.data);
      setShowModal(false); 
      setViewState('VIEW_SUBMISSIONS'); 
    } catch (error) {
      console.error("Error fetching candidates:", error);
      alert("Failed to load your submissions.");
    } finally {
      setLoadingCandidates(false);
    }
  };

  // --- RENDERERS ---

  const renderJobsList = () => (
    <>
      <div className="v-tabs-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
            <button 
            className={`v-tab-btn ${jobFilter === 'open' ? 'active' : ''}`}
            onClick={() => setJobFilter('open')}
            >
            Open Jobs
            </button>
            <button 
            className={`v-tab-btn ${jobFilter === 'closed' ? 'active' : ''}`}
            onClick={() => setJobFilter('closed')}
            >
            Closed Jobs
            </button>
        </div>

        {/* --- TOGGLE WITH NOTIFICATION BADGE --- */}
        <div 
            className="v-toggle-wrapper" 
            onClick={() => setShowBeeline(!showBeeline)} 
            style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '8px', 
                cursor: 'pointer',
                background: '#f8fafc',
                padding: '6px 12px',
                borderRadius: '20px',
                border: '1px solid #e2e8f0'
            }}
        >
            <span style={{ fontSize: '0.85rem', fontWeight: !showBeeline ? '700' : '500', color: !showBeeline ? '#0f172a' : '#64748b' }}>Standard</span>
            
            {showBeeline ? <ToggleRight size={28} color="#2563eb" /> : <ToggleLeft size={28} color="#94a3b8" />}
            
            <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                <span style={{ fontSize: '0.85rem', fontWeight: showBeeline ? '700' : '500', color: showBeeline ? '#2563eb' : '#64748b' }}>Beeline</span>
                
                {/* RED NOTIFICATION BADGE */}
                {beelineNotificationCount > 0 && (
                    <span style={{
                        position: 'absolute',
                        top: '-8px',
                        right: '-14px',
                        background: '#ef4444',
                        color: 'white',
                        fontSize: '0.65rem',
                        fontWeight: 'bold',
                        height: '18px',
                        minWidth: '18px',
                        padding: '0 5px',
                        borderRadius: '10px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: '0 2px 4px rgba(239, 68, 68, 0.3)'
                    }}>
                        {beelineNotificationCount}
                    </span>
                )}
            </div>
        </div>
      </div>

      <div className="v-jobs-grid">
        {filteredJobs.length > 0 ? (
          filteredJobs.map(job => (
            <div key={job._id} className="v-job-card" onClick={() => handleJobClick(job)}>
              <div className="v-job-header">
                <div className="v-company-icon">{job.companyName?.[0] || 'C'}</div>
                <div>
                  <h3>{job.role}</h3>
                  <p>{job.companyName}</p>
                  
                  {/* --- CONDITIONAL TAGS --- */}
                  {job.isBeeline && (
                      <div style={{marginTop: '4px'}}>
                        {job.assignedTo && job.assignedTo.includes(profileData?._id) ? (
                            <span style={{fontSize: '0.7rem', background: '#eff6ff', color: '#2563eb', padding: '2px 6px', borderRadius: '4px', border: '1px solid #bfdbfe', display: 'inline-block'}}>
                                Assigned to You
                            </span>
                        ) : (
                            <span style={{fontSize: '0.7rem', background: '#f0f9ff', color: '#0369a1', padding: '2px 6px', borderRadius: '4px', border: '1px solid #bae6fd', display: 'inline-block'}}>
                                Open Beeline Request
                            </span>
                        )}
                      </div>
                  )}
                </div>
                <ChevronRight className="v-arrow-icon" />
              </div>
              <div className="v-job-tags">
                <span><MapPin size={14}/> {job.location}</span>
                <span><DollarSign size={14}/> {job.ctc}</span>
                <span><Clock size={14}/> 
                {formatDate(job.applyByDate)} 
                {job.applyByTime ? ` ${job.applyByTime}` : ''}
                </span>
              </div>
            </div>
          ))
        ) : (
          <div className="v-empty-state">
              <div style={{marginBottom: '10px'}}>{showBeeline ? <Users size={48} color="#cbd5e1"/> : <Search size={48} color="#cbd5e1"/>}</div>
              No {jobFilter} {showBeeline ? "Beeline Requests" : "Standard Jobs"} found.
          </div>
        )}
      </div>
    </>
  );

  const renderCandidateForm = () => (
    <CandidateSubmissionForm 
      job={selectedJob} 
      vendor={profileData}
      onBack={handleBack} 
    />
  );

  // --- Submissions List ---
  const renderSubmissionsList = () => (
    <div className="v-preview-container" style={{ background: 'white', padding: '2rem', minHeight: '100vh' }}>
      <div style={{ marginBottom: '20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <button className="v-back-btn" onClick={handleBack} style={{ margin: 0 }}>
          <ArrowLeft size={16} /> Back to Jobs
        </button>
        <div style={{ textAlign: 'right' }}>
           <h2 style={{ margin: 0, fontSize: '1.2rem', color: '#1e293b' }}>{selectedJob?.role}</h2>
           <p style={{ margin: 0, color: '#64748b', fontSize: '0.9rem' }}>Submission Database</p>
        </div>
      </div>

      <div className="v-job-detail-card" style={{ maxWidth: '100%', padding: '0', overflow: 'hidden', border: '1px solid #e2e8f0', borderRadius: '12px' }}>
        {loadingCandidates ? (
          <div style={{ padding: '40px', textAlign: 'center' }}>Loading database...</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem', whiteSpace: 'nowrap' }}>
              <thead>
                <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0', textAlign: 'left', color: '#64748b', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  <th style={{ padding: '12px' }}>Name</th>
                  <th style={{ padding: '12px' }}>Email</th>
                  <th style={{ padding: '12px' }}>Contact</th>
                  <th style={{ padding: '12px' }}>Exp</th>
                  <th style={{ padding: '12px' }}>Current CTC</th>
                  <th style={{ padding: '12px' }}>Expected CTC</th>
                  <th style={{ padding: '12px' }}>Notice</th>
                  <th style={{ padding: '12px' }}>Status</th>
                  <th style={{ padding: '12px' }}>Resume</th>
                </tr>
              </thead>
              <tbody>
                {submittedCandidates.length > 0 ? (
                  submittedCandidates.map((cand) => (
                    <tr key={cand._id} style={{ borderBottom: '1px solid #f1f5f9', color: '#334155' }}>
                      <td style={{ padding: '12px', fontWeight: '600', color: '#1e293b' }}>{cand.candidateName}</td>
                      <td style={{ padding: '12px', color: '#475569' }}>{cand.candidateEmail}</td>
                      <td style={{ padding: '12px' }}>{cand.contactNumber || '--'}</td>
                      <td style={{ padding: '12px' }}>{cand.totalExperience || '0'}</td>
                      <td style={{ padding: '12px' }}>{cand.currentCTC || '--'}</td>
                      <td style={{ padding: '12px' }}>{cand.expectedCTC || '--'}</td>
                      <td style={{ padding: '12px', color: '#d97706', fontWeight: '500' }}>{cand.noticePeriod || '--'}</td>
                      <td style={{ padding: '12px' }}>
                        <span style={{
                          padding: '4px 10px',
                          borderRadius: '12px',
                          fontSize: '0.75rem',
                          fontWeight: '600',
                          background: (cand.candidateStatus?.includes('Select')) ? '#dcfce7' : (cand.candidateStatus?.includes('Reject')) ? '#fee2e2' : '#fef9c3',
                          color: (cand.candidateStatus?.includes('Select')) ? '#166534' : (cand.candidateStatus?.includes('Reject')) ? '#991b1b' : '#854d0e'
                        }}>
                          {cand.candidateStatus || 'Pending Review'}
                        </span>
                      </td>
                      <td style={{ padding: '12px' }}>
                        {cand.isBeeline ? (
                           cand.originalResumeUrl ? (
                             <a 
                               href={getFreshFileUrl(cand.originalResumeUrl)} 
                               target="_blank" 
                               rel="noreferrer" 
                               style={{ color: '#2563eb', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '5px' }}
                               title="Original Resume"
                             >
                               <Download size={14} /> PDF
                             </a>
                           ) : <span style={{ color: '#cbd5e1' }}>--</span>
                        ) : cand.resumeFile && cand.resumeFile.fileUrl ? (
                           <a 
                             href={getFreshFileUrl(cand.resumeFile.fileUrl)} 
                             target="_blank" 
                             rel="noreferrer" 
                             style={{ color: '#2563eb', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '5px' }}
                           >
                             <Download size={14} /> PDF
                           </a>
                        ) : <span style={{ color: '#cbd5e1' }}>--</span>}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="9" style={{ padding: '40px', textAlign: 'center', color: '#94a3b8' }}>
                      You haven't submitted any candidates for this job yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );

  const renderProfile = () => {
    const displayData = profileData || {}; 

    if (viewState === 'EDIT_PROFILE') {
       return (
         <div>
           <button className="v-back-btn" onClick={() => { setViewState('LIST'); setActiveTab('profile'); }}>
             <ArrowLeft size={16}/> Cancel Edit
           </button>
           <VendorProfileForm initialData={displayData} onSubmit={handleProfileUpdate} />
         </div>
       );
    }
    
    return (
      <div className="v-profile-view">
        <div className="v-profile-header-card">
           <div className="v-profile-avatar">{displayData.vendorName?.charAt(0) || 'V'}</div>
           <div>
             <h1>{displayData.vendorName || 'Vendor Name'}</h1>
             <p>ID: {displayData.vendorId || '--'}</p>
           </div>
           <button className="v-edit-profile-btn" onClick={() => setViewState('EDIT_PROFILE')}>
             <Edit size={16} /> Edit Profile
           </button>
        </div>
        
        <div className="v-profile-details-grid">
           <div className="v-detail-box">
             <h4>Contact Info</h4>
             <p><strong>Email:</strong> {displayData.vendorEmail || '--'}</p>
             <p><strong>Phone:</strong> {displayData.vendorPhone || '--'}</p>
           </div>
           <div className="v-detail-box">
             <h4>Address</h4>
             <p>{displayData.street || 'Address not provided'}</p>
             <p>{displayData.city ? `${displayData.city}, ${displayData.state}` : ''}</p>
           </div>
        </div>
      </div>
    );
  };

  const JobDetailsModal = () => {
    if (!showModal || !selectedJob) return null;
    const open = isJobOpen(selectedJob);

    const fileUrl = selectedJob.fileUrl || selectedJob.localJdFile;
    const finalLink = getFreshFileUrl(fileUrl);

    return (
      <div className="modal-overlay" onClick={() => setShowModal(false)}>
        <div className="modal-content" onClick={e => e.stopPropagation()}>
          
          <div className="modal-header">
            <div>
              <h2>{selectedJob.role}</h2>
              <p>{selectedJob.companyName} • {selectedJob.location} • {selectedJob.typeOfWork}</p>
              
              {/* --- SHOW BEELINE INFO IN MODAL --- */}
              {selectedJob.isBeeline && (
                  <div style={{marginTop: '8px', display: 'flex', gap: '10px', flexWrap: 'wrap'}}>
                      {selectedJob.assignedTo?.includes(profileData?._id) ? (
                          <span style={{fontSize: '0.8rem', background: '#eff6ff', color: '#2563eb', padding: '4px 8px', borderRadius: '4px', border: '1px solid #bfdbfe', fontWeight: '600'}}>
                              Assigned to You
                          </span>
                      ) : (
                          <span style={{fontSize: '0.8rem', background: '#f0f9ff', color: '#0369a1', padding: '4px 8px', borderRadius: '4px', border: '1px solid #bae6fd', fontWeight: '600'}}>
                              Open Beeline Request
                          </span>
                      )}
                      
                      {selectedJob.requestId && (
                          <span style={{fontSize: '0.8rem', color: '#64748b', alignSelf:'center', fontWeight: '500'}}>
                              Req ID: {selectedJob.requestId}
                          </span>
                      )}
                      {selectedJob.beelineId && (
                          <span style={{fontSize: '0.8rem', color: '#64748b', alignSelf:'center', fontWeight: '500'}}>
                              Beeline ID: {selectedJob.beelineId}
                          </span>
                      )}
                  </div>
              )}
            </div>
            <button className="close-btn" onClick={() => setShowModal(false)}>
              <X size={20} />
            </button>
          </div>
          
          <div className="modal-body">
            <div className="detail-section">
              <div className="info-grid-3">
                <div className="info-item">
                  <label>CTC / Stipend</label>
                  <span style={{color: '#059669'}}>{selectedJob.ctc}</span>
                </div>
                <div className="info-item">
                  <label>Batch</label>
                  <span>{selectedJob.batch || 'Any'}</span>
                </div>
                <div className="info-item">
                   <label>Apply By</label>
                   <span style={{color: '#dc2626'}}>
                     {formatDate(selectedJob.applyByDate)} 
                     {selectedJob.applyByTime ? ` ${selectedJob.applyByTime}` : ''}
                   </span>
                </div>
              </div>
            </div>

            <div className="detail-section">
              <h4><CheckCircle size={14} /> Eligibility Criteria</h4>
              <div className="info-grid-3" style={{ rowGap: '24px' }}>
                <div className="info-item"><label>10th Percentage</label><span>{selectedJob.tenth || '--'}%</span></div>
                <div className="info-item"><label>12th Percentage</label><span>{selectedJob.twelfth || '--'}%</span></div>
                <div className="info-item"><label>Min CGPA</label><span>{selectedJob.minCGPA || '--'}</span></div>
                <div className="info-item"><label>Passout Year</label><span>{selectedJob.passoutYear || 'Any'}</span></div>
                <div className="info-item"><label>Allowed Gender</label><span>{selectedJob.gender}</span></div>
                <div className="info-item"><label>Backlogs</label><span>{selectedJob.maxHistoryArrears} / {selectedJob.maxCurrentArrears}</span></div>
              </div>
            </div>

            <div className="detail-section">
              <h4>About The Job</h4>
              <div className="text-block">{selectedJob.aboutJob || 'No description provided.'}</div>
            </div>

            {selectedJob.aboutCompany && (
              <div className="detail-section">
                <h4>About Company</h4>
                <div className="text-block">{selectedJob.aboutCompany}</div>
              </div>
            )}

            {selectedJob.hiringWorkflows && (
              <div className="detail-section">
                <h4>Hiring Workflow</h4>
                <div className="text-block">{selectedJob.hiringWorkflows}</div>
              </div>
            )}

            {fileUrl && (
              <div className="detail-section">
                <h4><FileText size={16} style={{marginBottom:'-2px', marginRight: '5px'}}/> Attached JD</h4>
                <div style={{ background: '#f1f5f9', padding: '12px', borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', border: '1px solid #e2e8f0' }}>
                    <div style={{display:'flex', alignItems:'center', gap:'10px'}}>
                        <FileText size={20} color="#475569"/>
                        <div>
                           <div style={{ fontSize: '0.9rem', color: '#334155', fontWeight:'500' }}>Job Description Document</div>
                           <div style={{ fontSize: '0.75rem', color: '#64748b' }}>Click to download/view</div>
                        </div>
                    </div>
                    <a href={finalLink} target="_blank" rel="noreferrer" style={{ textDecoration: 'none', color: '#2563eb', display: 'flex', alignItems: 'center', gap: '5px', fontSize: '0.9rem', fontWeight: '500', background:'white', padding:'6px 12px', borderRadius:'4px', border:'1px solid #cbd5e1' }}>
                        <Download size={16}/> Download
                    </a>
                </div>
              </div>
            )}

            {(selectedJob.externalLink || selectedJob.additionalInfo) && (
              <div className="detail-section" style={{ marginBottom: 0 }}>
                <h4>Additional Info</h4>
                {selectedJob.externalLink && (
                  <a href={selectedJob.externalLink} target="_blank" rel="noreferrer" className="external-link-box">
                    <Info size={18} />
                    Apply Externally: {selectedJob.externalLink}
                  </a>
                )}
                {selectedJob.additionalInfo && (
                  <div style={{ marginTop: '12px', fontSize: '0.9rem', color: '#64748b', fontStyle: 'italic' }}>
                    Note: {selectedJob.additionalInfo}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="modal-footer" style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '20px', borderTop: '1px solid #f1f5f9', paddingTop: '15px' }}>
            {open ? (
              <>
                <button 
                  onClick={handleViewSubmissions}
                  style={{ 
                    background: 'white', 
                    color: '#1e293b', 
                    border: '1px solid #cbd5e1', 
                    padding: '0.75rem 1.25rem',
                    borderRadius: '8px', 
                    fontWeight: '600',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  <Users size={18} /> View Submitted
                </button>

                {/* --- SMART SUBMIT BUTTON --- */}
                <button className="v-modal-submit-btn" onClick={handleStartSubmission}>
                  <Send size={18} /> {selectedJob.isBeeline ? "Submit to Beeline" : "Submit Candidate"}
                </button>
              </>
            ) : (
               <div style={{ color: '#ef4444', fontWeight: '500', fontSize: '0.9rem' }}>
                 Applications are closed.
               </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // CHECK LOADING STATE BEFORE RENDERING ANYTHING
  if (loadingProfile) return <div style={{padding:'20px', textAlign: 'center'}}>Loading...</div>;

  return (
    <div className="vendor-dashboard-container">
      <aside className={`vendor-sidebar ${sidebarOpen ? 'open' : ''}`}>
        <button className="v-sidebar-close" onClick={() => setSidebarOpen(false)}>
          <X size={18} />
        </button>
        <div className="v-sidebar-header">
          <div className="v-logo-icon"><LayoutDashboard size={24} color="white" /></div>
          <h2>Partner Portal</h2>
        </div>
        <nav className="v-sidebar-nav">
          <button className={`v-nav-item ${activeTab === 'browse-jobs' ? 'active' : ''}`} onClick={() => { setActiveTab('browse-jobs'); setViewState('LIST'); setSidebarOpen(false); }}>
            <Search size={20} /><span>Browse Jobs</span>
          </button>

          <button 
            className={`v-nav-item ${activeTab === 'all-submissions' ? 'active' : ''}`} 
            onClick={() => { setActiveTab('all-submissions'); setViewState('LIST'); setSidebarOpen(false); }}
          >
            <Database size={20} /><span>Candidates Applied</span>
          </button>
          <button 
            className={`v-nav-item ${activeTab === 'manage-candidates' ? 'active' : ''}`} 
            onClick={() => { setActiveTab('manage-candidates'); setViewState('LIST'); setSidebarOpen(false); }}
          >
            <LayoutDashboard size={20} /><span>Manage Candidates</span>
          </button>
          
          
          <button className={`v-nav-item ${activeTab === 'profile' ? 'active' : ''}`} onClick={() => { setActiveTab('profile'); setViewState('LIST'); setSidebarOpen(false); }}>
            <User size={20} /><span>My Profile</span>
          </button>
        </nav>
        <div className="v-sidebar-footer">
          <button className="v-nav-item v-logout-btn" onClick={onLogout}><LogOut size={20} /><span>Log Out</span></button>
        </div>
      </aside>
      {sidebarOpen && <div className="v-sidebar-overlay" onClick={() => setSidebarOpen(false)} />}

      <main className="v-main-content">
        <div className="v-top-bar">
          <button className="v-hamburger-btn" onClick={() => setSidebarOpen(true)}>
            <Menu size={20} />
          </button>
          <h2>{activeTab === 'browse-jobs' ? 'Job Market' : activeTab === 'all-submissions' ? 'Submissions Database' : activeTab === 'manage-candidates' ? 'Manage Candidates' : 'My Profile'}</h2>
          <div className="v-user-pill" onClick={() => { setActiveTab('profile'); setViewState('LIST'); }} style={{cursor: 'pointer'}}>
            <div className="v-avatar">{profileData?.vendorName?.charAt(0) || 'V'}</div>
            <span>{profileData?.vendorName || 'My Profile'}</span>
          </div>
        </div>
        
        <div 
          className="v-content-wrapper" 
          style={(activeTab === 'all-submissions' || activeTab === 'manage-candidates') ? { maxWidth: '100%', width: '100%', padding: '20px' } : {}}
        >
          {activeTab === 'browse-jobs' && viewState === 'LIST' && renderJobsList()}
          {activeTab === 'browse-jobs' && viewState === 'SUBMIT_CANDIDATE' && renderCandidateForm()}
          
          {/* --- RENDER BEELINE FORM IF SELECTED --- */}
          {activeTab === 'browse-jobs' && viewState === 'SUBMIT_BEELINE' && (
              <BeelineSubmissionForm
                  job={selectedJob}
                  partner={profileData}
                  onBack={handleBack}
              />
          )}
          
          {activeTab === 'browse-jobs' && viewState === 'VIEW_SUBMISSIONS' && renderSubmissionsList()}
          
          {activeTab === 'all-submissions' && (
             <VendorSubmissionsDatabase vendorId={profileData?._id} />
          )}
          {activeTab === 'manage-candidates' && (
             <PostedJobs partnerId={profileData?._id} />
          )}
          {activeTab === 'profile' && renderProfile()}
        </div>
      </main>

      <JobDetailsModal />
    </div>
  );
}